import { useState } from 'react'
import cn from 'clsx'
import { ResponsiveImage } from '../responsive-image'
import { buildImageCandidates } from '../responsive-image/imageCandidates'
import type { TCoverProps } from './Cover.types'
import styles from './Cover.module.css'
import placeholderDesktop from './cover-placeholder.jpg'
import placeholderMobile from './cover-placeholder-mobile.jpg'

export const Cover = ({
    src,
    alt,
    sizes,
    resize,
    priority,
    width,
    height,
    fill = true,
    className,
    stacked,
    shouldScale = true,
    scaledOnMobile = false,
    placeholderSrc = placeholderDesktop,
    placeholderMobileSrc = placeholderMobile,
}: TCoverProps) => {
    // Tracking the failed src (instead of a boolean) resets the fallback when `src` changes.
    const [erroredSrc, setErroredSrc] = useState<string | null>(null)

    const handleError = () => setErroredSrc(src ?? null)

    const image =
        src && erroredSrc !== src ? (
            <ResponsiveImage
                fill={fill}
                sizes={sizes}
                alt={alt}
                src={src}
                resize={resize}
                priority={priority}
                width={width}
                height={height}
                className={styles.image}
                onError={handleError}
            />
        ) : (
            <ResponsiveImage
                src={placeholderMobileSrc}
                sources={[
                    {
                        srcSet:
                            buildImageCandidates({
                                src: placeholderSrc,
                                resize,
                                width: sizes ? undefined : width,
                                sizes,
                            }).srcSet ?? placeholderSrc,
                        media: '(min-width: 768px)',
                    },
                ]}
                sizes={sizes}
                resize={resize}
                width={width}
                height={height}
                alt={alt}
                className={cn(styles.image, fill && styles.fill)}
            />
        )

    if (!stacked) {
        return <div className={cn(styles.frame, className)}>{image}</div>
    }

    return (
        <div className={cn(styles.stacked, className)}>
            <div
                className={cn(styles.backLayer, {
                    [styles.backScaleTablet]: shouldScale,
                    [styles.backScaleMobile]: scaledOnMobile,
                })}
            >
                {image}
                <div className={styles.tint} />
            </div>
            <div
                className={cn(styles.frontLayer, {
                    [styles.frontScaleTablet]: shouldScale,
                    [styles.frontScaleMobile]: scaledOnMobile,
                })}
            >
                {image}
            </div>
        </div>
    )
}
